import { useCallback, useEffect, useRef, useState } from "react";
import { Client, Frame, Message } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

type NotificationType = 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR';

interface Notification {
    id?: string;
    type: NotificationType;
    title: string;
    message: string;
    userId?: string;
    timestamp?: string;
}

interface WebSocketMessage {
    id?: string;           //  ADD THIS - backend may send id
    type: NotificationType;
    title: string;
    message: string;
    userId?: string;
    timestamp?: string;
}

interface UseWebSocketOptions {
    url: string;
    onMessage?: (message: WebSocketMessage) => void;
    onConnect?: () => void;
    onDisconnect?: () => void;
    reconnectInterval?: number;
}

export const useWebSocket = ({
    url,
    onMessage,
    onConnect,
    onDisconnect,
    reconnectInterval = 5000,
}: UseWebSocketOptions) => {
    const [isConnected, setIsConnected] = useState(false);
    const stompClientRef = useRef<Client | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    //  FIX: Store callbacks in refs to avoid reconnection loops
    const onMessageRef = useRef(onMessage);
    const onConnectRef = useRef(onConnect);
    const onDisconnectRef = useRef(onDisconnect);

    //  Update refs when callbacks change (but don't trigger re-renders)
    useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
    useEffect(() => { onConnectRef.current = onConnect; }, [onConnect]);
    useEffect(() => { onDisconnectRef.current = onDisconnect; }, [onDisconnect]);

    const connect = useCallback(() => {
        //console.log('🔌 Connecting to WebSocket:', url);

        // Clean up any existing client first
        if (stompClientRef.current) 
        {
            //console.log('🔌 Cleaning up existing STOMP client...');
            stompClientRef.current.deactivate();
            stompClientRef.current = null;
        }

        const client = new Client({
            webSocketFactory: () => new SockJS(url, null, {
                transports: ['websocket', 'xhr-streaming', 'xhr-polling'], // skip iframe transport
            }),
            heartbeatIncoming: 0,
            heartbeatOutgoing: 0,
            //debug: (str) => console.log('📡 STOMP Debug:', str),
            reconnectDelay: reconnectInterval,
            connectHeaders: {},

            onConnect: (frame: Frame) => {
                // console.log(' STOMP Connected', {
                //     command: frame.command,
                //     session: frame.headers['session'],
                //     heartBeat: frame.headers['heart-beat'],
                // });
                setIsConnected(true);
                //  Use ref to call callback (stable reference)
                onConnectRef.current?.();

                client.subscribe('/topic/notifications', (message: Message) => {
                    try 
                    {
                        if (!message.body) return;
                        const parsed: WebSocketMessage = JSON.parse(message.body);
                        //console.log('📨 Received:', parsed);
                        //  Use ref to call callback
                        onMessageRef.current?.(parsed);
                    } 
                    catch (error) 
                    {
                        console.error('❌ Parse error:', error);
                    }
                });
                //console.log('📡 Subscribed to /topic/notifications');
            },

            onStompError: (frame: Frame) => {
                console.error('❌ STOMP Error:', frame.headers['message'], frame.body);
            },

            onWebSocketClose: (event: CloseEvent) => {
                console.log('🔌 WebSocket Closed', {
                    code: event.code,
                    reason: event.reason,
                    wasClean: event.wasClean,
                });
                setIsConnected(false);
                //  Use ref to call callback
                onDisconnectRef.current?.();
            },

            onWebSocketError: (event: Event) => {
                console.error('⚠️ WebSocket Error:', event);
            },
        });

        stompClientRef.current = client;
        client.activate();
        //console.log('📡 STOMP client activated');
    }, [url, reconnectInterval]);  //  Only depends on stable values

    const disconnect = useCallback(() => {
        console.log('🔌 Disconnecting STOMP client...');

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (stompClientRef.current) {
            stompClientRef.current.deactivate();
            stompClientRef.current = null;
            console.log('🔌 STOMP client deactivated');
        }
        setIsConnected(false);
    }, []);  //  No dependencies = stable reference

    const sendMessage = useCallback((destination: string, body: unknown): boolean => {
        if (stompClientRef.current?.connected) {
            stompClientRef.current.publish({
                destination,
                body: JSON.stringify(body),
            });
            return true;
        }
        console.warn('⚠️ STOMP not connected');
        return false;
    }, []);

    //  FIX: useEffect with stable dependencies (no callback deps!)
    useEffect(() => {
        connect();
        return () => {
            disconnect();
        };
    }, [connect, disconnect]);  //  These are now stable due to useCallback

    return {
        isConnected,
        sendMessage,
        connect,
        disconnect,
    };
};